/**
 * 🛡️ G2GENTE - Mobile Profile Page
 * Tela completa de perfil do candidato/funcionário com todas as seções
 * Suporta tanto role CANDIDATE quanto EMPLOYEE
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { candidatesAPI, employeeSelfAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft,
  Loader2,
  User,
  Briefcase,
  GraduationCap,
  Award,
  Brain,
  Heart,
  MapPin,
  Mail,
  Phone,
  Building,
  Calendar,
  Edit,
  Shield,
  CheckCircle,
  Clock,
  Home,
  Globe,
  Star,
  FileText,
  Download,
  Camera,
  IdCard,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { ResumeGeneratorDialog } from '../../components/resume/ResumeGeneratorDialog';

// DISC Profile descriptions
const DISC_DESCRIPTIONS = {
  D: {
    name: 'Dominância',
    color: 'bg-red-100 text-red-700',
    description: 'Perfil orientado a resultados, decisivo e competitivo.',
    pontos_fortes: ['Liderança', 'Foco em resultados', 'Tomada de decisão rápida', 'Competitividade'],
    pontos_atencao: ['Pode ser impaciente', 'Tendência a dominar discussões', 'Dificuldade em delegar']
  },
  I: {
    name: 'Influência',
    color: 'bg-yellow-100 text-yellow-700',
    description: 'Perfil comunicativo, entusiasta e otimista.',
    pontos_fortes: ['Comunicação', 'Networking', 'Criatividade', 'Motivação de equipes'],
    pontos_atencao: ['Pode perder foco', 'Otimismo excessivo', 'Dificuldade com detalhes']
  },
  S: {
    name: 'Estabilidade',
    color: 'bg-green-100 text-green-700',
    description: 'Perfil colaborativo, paciente e confiável.',
    pontos_fortes: ['Trabalho em equipe', 'Paciência', 'Lealdade', 'Escuta ativa'],
    pontos_atencao: ['Resistência a mudanças', 'Dificuldade em dizer não', 'Evita confrontos']
  },
  C: {
    name: 'Conformidade',
    color: 'bg-blue-100 text-blue-700',
    description: 'Perfil analítico, preciso e orientado a qualidade.',
    pontos_fortes: ['Análise crítica', 'Atenção a detalhes', 'Organização', 'Qualidade'],
    pontos_atencao: ['Perfeccionismo', 'Análise excessiva', 'Crítico demais']
  }
};

export default function MobileProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEmployee, setIsEmployee] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      // Detectar se é funcionário ou candidato
      const userRole = user?.role?.toUpperCase();
      
      if (userRole === 'EMPLOYEE') {
        // Funcionário - usar employeeSelfAPI
        setIsEmployee(true);
        const response = await employeeSelfAPI.getMe();
        setProfile(response.data);
      } else {
        // Candidato - usar candidatesAPI
        setIsEmployee(false);
        const response = await candidatesAPI.getMe();
        setProfile(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Redirecionar para edição se perfil não existe
        const editPath = isEmployee ? '/m/employee/profile/edit' : '/m/profile/edit';
        navigate(editPath);
      } else {
        toast.error('Erro ao carregar perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDISCInfo = (discProfile) => {
    if (!discProfile) return null;
    const primary = discProfile.charAt(0).toUpperCase();
    return DISC_DESCRIPTIONS[primary] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Caminho de edição baseado no tipo de usuário
  const editPath = isEmployee ? '/m/employee/profile/edit' : '/m/profile/edit';

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <User className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Perfil não encontrado</h2>
        <p className="text-slate-600 text-center mb-6">Complete seu perfil para começar.</p>
        <Button onClick={() => navigate(editPath)} className="bg-cyan-600 hover:bg-cyan-700">
          <Edit className="w-4 h-4 mr-2" />
          Criar Perfil
        </Button>
      </div>
    );
  }

  const discInfo = getDISCInfo(profile.disc_profile);
  const discDetalhado = profile.disc_detalhado || {};
  
  // Build photo URL
  const photoUrl = profile.foto_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${profile.foto_url}`
    : null;

  // Share profile function
  const handleShareProfile = async () => {
    const shareData = {
      title: `${profile.nome || 'Candidato'} - ${profile.cargo || 'Profissional'}`,
      text: `Confira o perfil profissional de ${profile.nome || 'candidato'} na plataforma G2Gente`,
      url: window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Perfil compartilhado!');
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a área de transferência!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // User cancelled share
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a área de transferência!');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="mobile-profile">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-4 pt-8 pb-20 rounded-b-3xl">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/m')} className="text-slate-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleShareProfile}
              className="text-slate-400 hover:text-slate-300 hover:bg-slate-700"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(editPath)}
              className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-700"
            >
              <Edit className="w-4 h-4 mr-1" />
              Editar
            </Button>
          </div>
        </div>
        
        {/* Profile Header */}
        <div className="text-center">
          <div className="relative inline-block">
            {photoUrl ? (
              <img 
                src={photoUrl} 
                alt={profile.nome || 'Foto'} 
                className="w-24 h-24 rounded-full object-cover border-4 border-cyan-500 shadow-lg mx-auto"
              />
            ) : (
              <div className="w-24 h-24 bg-cyan-600 rounded-full flex items-center justify-center mx-auto shadow-lg border-4 border-cyan-500">
                <User className="w-12 h-12 text-white" />
              </div>
            )}
            <button 
              onClick={() => navigate(editPath)}
              className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-slate-200"
            >
              <Camera className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          <h1 className="text-2xl font-bold text-white mt-3">{profile.nome || 'Candidato'}</h1>
          <p className="text-cyan-400 font-medium">{profile.cargo}</p>
          {profile.localizacao && (
            <p className="text-slate-400 text-sm flex items-center justify-center gap-1 mt-2">
              <MapPin className="w-4 h-4" />
              {profile.localizacao}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-12 space-y-4">
        
        {/* STATUS - ABERTO A OPORTUNIDADES */}
        {profile.aberto_a_oportunidades !== undefined && (
          <Card className={`border-2 shadow-sm ${profile.aberto_a_oportunidades ? 'border-green-300 bg-green-50' : 'border-slate-300 bg-slate-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${profile.aberto_a_oportunidades ? 'bg-green-200' : 'bg-slate-200'}`}>
                  <Briefcase className={`w-5 h-5 ${profile.aberto_a_oportunidades ? 'text-green-600' : 'text-slate-500'}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${profile.aberto_a_oportunidades ? 'text-green-700' : 'text-slate-600'}`}>
                    {profile.aberto_a_oportunidades ? 'Aberto a novas oportunidades' : 'Não está buscando vagas'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {profile.aberto_a_oportunidades 
                      ? 'Seu perfil aparece nas buscas e você recebe vagas' 
                      : 'Seu perfil não aparece nas buscas das empresas'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {!isEmployee && (
          <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-white shadow-sm" data-testid="resume-generator-card">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-cyan-700" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">Currículo G2eGente</p>
                  <p className="text-xs text-slate-500">PDF pronto para enviar às empresas</p>
                </div>
              </div>
              <Button size="sm" onClick={() => setResumeOpen(true)} className="bg-cyan-600 hover:bg-cyan-700 shrink-0" data-testid="open-resume-generator">
                <Download className="w-4 h-4 mr-1" />Gerar
              </Button>
            </CardContent>
          </Card>
        )}
        <ResumeGeneratorDialog open={resumeOpen} onClose={() => setResumeOpen(false)} email={user?.email} />

        {/* CURRÍCULO PDF */}
        {profile.curriculo_url && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{profile.curriculo_nome || 'Currículo.pdf'}</p>
                    <p className="text-xs text-slate-500">Arquivo PDF anexado</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${profile.curriculo_url}`, '_blank')}
                    className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Baixar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 1. DADOS PESSOAIS */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-600" />
              Dados Pessoais
            </CardTitle>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Visível apenas para você
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* CPF */}
            {profile.cpf && (
              <>
                <div className="flex items-center gap-3">
                  <IdCard className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">CPF</p>
                    <p className="text-sm font-medium text-slate-900 font-mono">
                      {profile.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                    </p>
                  </div>
                </div>
                <Separator />
              </>
            )}
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">E-mail</p>
                <p className="text-sm font-medium text-slate-900">{profile.email || user?.email || '-'}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Celular</p>
                <p className="text-sm font-medium text-slate-900">{profile.telefone || 'Não informado'}</p>
              </div>
            </div>
            <Separator />
            {/* Data de Nascimento */}
            {profile.data_nascimento && (
              <>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Data de Nascimento</p>
                    <p className="text-sm font-medium text-slate-900">
                      {new Date(profile.data_nascimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                      <span className="text-slate-400 ml-2">
                        ({Math.floor((new Date() - new Date(profile.data_nascimento)) / (365.25 * 24 * 60 * 60 * 1000))} anos)
                      </span>
                    </p>
                  </div>
                </div>
                <Separator />
              </>
            )}
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Cidade / Estado</p>
                <p className="text-sm font-medium text-slate-900">
                  {profile.cidade && profile.estado 
                    ? `${profile.cidade} / ${profile.estado}` 
                    : profile.localizacao || 'Não informado'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. PERFIL PROFISSIONAL */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-cyan-600" />
              Perfil Profissional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cargo Pretendido */}
            <div>
              <p className="text-xs text-slate-500 mb-1">Cargo Pretendido</p>
              <p className="text-sm font-semibold text-slate-900">{profile.cargo}</p>
            </div>
            
            <Separator />
            
            {/* Experiências */}
            <div>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <Building className="w-3 h-3" />
                Experiência Profissional
              </p>
              {profile.experiencias && profile.experiencias.length > 0 ? (
                <div className="space-y-3">
                  {profile.experiencias.map((exp, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{exp.cargo}</p>
                          <p className="text-sm text-cyan-600">{exp.empresa}</p>
                        </div>
                        {exp.atual && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            Atual
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {exp.periodo_inicio} - {exp.atual ? 'Presente' : exp.periodo_fim}
                      </p>
                      {exp.descricao && (
                        <p className="text-xs text-slate-600 mt-2">{exp.descricao}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                  {profile.experiencia || 'Nenhuma experiência cadastrada'}
                </p>
              )}
            </div>
            
            <Separator />
            
            {/* Formação */}
            <div>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <GraduationCap className="w-3 h-3" />
                Formação Acadêmica
              </p>
              {profile.formacoes && profile.formacoes.length > 0 ? (
                <div className="space-y-2">
                  {profile.formacoes.map((form, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3">
                      <p className="font-medium text-slate-900">{form.curso}</p>
                      <p className="text-sm text-slate-600">{form.instituicao}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{form.nivel}</Badge>
                        {form.em_andamento ? (
                          <span className="text-xs text-amber-600">Em andamento</span>
                        ) : form.ano_conclusao && (
                          <span className="text-xs text-slate-500">{form.ano_conclusao}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">Nenhuma formação cadastrada</p>
              )}
            </div>
            
            <Separator />
            
            {/* Cursos */}
            <div>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                <Award className="w-3 h-3" />
                Cursos Complementares
              </p>
              {profile.cursos && profile.cursos.length > 0 ? (
                <div className="space-y-2">
                  {profile.cursos.map((curso, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{curso.nome}</p>
                        <p className="text-xs text-slate-500">{curso.instituicao}</p>
                      </div>
                      {curso.ano && (
                        <span className="text-xs text-slate-400">{curso.ano}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">Nenhum curso cadastrado</p>
              )}
            </div>
            
            <Separator />
            
            {/* Competências */}
            <div>
              <p className="text-xs text-slate-500 mb-2">Competências Técnicas</p>
              {profile.competencias && profile.competencias.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.competencias.map((comp, index) => (
                    <Badge key={index} variant="secondary" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                      {comp}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">Nenhuma competência cadastrada</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 3. PERFIL COMPORTAMENTAL (DISC) */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="w-5 h-5 text-cyan-600" />
              Perfil Comportamental (DISC)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile.disc_profile || discDetalhado.resultado_dominante ? (
              <div className="space-y-4">
                {/* Resultado Dominante */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Resultado Dominante</p>
                    <div className="flex items-center gap-2 mt-1">
                      {(discDetalhado.resultado_dominante || profile.disc_profile)?.split('').map((letter, i) => {
                        const info = DISC_DESCRIPTIONS[letter.toUpperCase()];
                        return info ? (
                          <Badge key={i} className={`${info.color} font-bold`}>
                            {letter.toUpperCase()} - {info.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                  {discDetalhado.aderencia_cargo && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Aderência ao Cargo</p>
                      <p className="text-lg font-bold text-cyan-600">{discDetalhado.aderencia_cargo}%</p>
                    </div>
                  )}
                </div>
                
                {/* Texto Explicativo */}
                {(discDetalhado.texto_explicativo || discInfo?.description) && (
                  <>
                    <Separator />
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-slate-700">
                        {discDetalhado.texto_explicativo || discInfo?.description}
                      </p>
                    </div>
                  </>
                )}
                
                {/* Pontos Fortes */}
                {(discDetalhado.pontos_fortes || discInfo?.pontos_fortes) && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                        <Star className="w-3 h-3 text-green-500" />
                        Pontos Fortes
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(discDetalhado.pontos_fortes || discInfo?.pontos_fortes)?.map((ponto, i) => (
                          <Badge key={i} className="bg-green-50 text-green-700 border-green-200">
                            {ponto}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                
                {/* Pontos de Atenção */}
                {(discDetalhado.pontos_atencao || discInfo?.pontos_atencao) && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-amber-500" />
                        Pontos de Atenção
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(discDetalhado.pontos_atencao || discInfo?.pontos_atencao)?.map((ponto, i) => (
                          <Badge key={i} variant="outline" className="text-amber-700 border-amber-200">
                            {ponto}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <Brain className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-3">Você ainda não realizou o questionário DISC</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/m/disc')}
                  className="text-cyan-600 border-cyan-200"
                >
                  Fazer Avaliação DISC
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. INCLUSÃO */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="w-5 h-5 text-cyan-600" />
              Inclusão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Elegibilidade */}
            {(profile.is_pcd || profile.is_50_plus) ? (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-900">Elegível a Políticas de Inclusão</span>
                </div>
                <p className="text-xs text-purple-700">
                  Seu perfil está marcado como elegível para vagas com políticas de inclusão. 
                  As empresas verão apenas esta informação, sem especificar a categoria.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">Nenhuma elegibilidade informada</p>
            )}
            
            <Separator />
            
            {/* Consentimento */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className={`w-5 h-5 ${profile.consent_share ? 'text-green-500' : 'text-slate-400'}`} />
                <div>
                  <p className="font-medium text-slate-900">Compartilhamento de Dados</p>
                  <p className="text-xs text-slate-500">Permite que empresas vejam seu perfil</p>
                </div>
              </div>
              <Badge className={profile.consent_share ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                {profile.consent_share ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            
            {profile.observacoes_inclusao && (
              <>
                <Separator />
                <div>
                  <p className="text-xs text-slate-500 mb-1">Observações</p>
                  <p className="text-sm text-slate-700">{profile.observacoes_inclusao}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 5. DISPONIBILIDADE */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-600" />
              Disponibilidade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Tipo de Contrato */}
            <div>
              <p className="text-xs text-slate-500 mb-2">Tipo de Contrato Desejado</p>
              {profile.tipo_contrato_desejado && profile.tipo_contrato_desejado.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.tipo_contrato_desejado.map((tipo, i) => (
                    <Badge key={i} variant="secondary">{tipo}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">Não informado</p>
              )}
            </div>
            
            <Separator />
            
            {/* Mudança */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-900">Disponível para Mudança</span>
              </div>
              <Badge className={profile.disponibilidade_mudanca ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                {profile.disponibilidade_mudanca ? 'Sim' : 'Não'}
              </Badge>
            </div>
            
            <Separator />
            
            {/* Remoto */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {profile.disponibilidade_remoto === 'Remoto' ? (
                  <Home className="w-4 h-4 text-slate-400" />
                ) : profile.disponibilidade_remoto === 'Híbrido' ? (
                  <Globe className="w-4 h-4 text-slate-400" />
                ) : (
                  <Building className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-sm text-slate-900">Modalidade de Trabalho</span>
              </div>
              <Badge variant="outline">
                {profile.disponibilidade_remoto || 'Não informado'}
              </Badge>
            </div>
            
            {profile.pretensao_salarial && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-900">Pretensão Salarial</span>
                  <span className="font-medium text-cyan-600">{profile.pretensao_salarial}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* CURRÍCULO */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" />
              Currículo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile.curriculo_url ? (
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{profile.curriculo_nome || 'curriculo.pdf'}</p>
                    <p className="text-xs text-slate-500">PDF</p>
                  </div>
                </div>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}${profile.curriculo_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-full"
                >
                  <Download className="w-5 h-5" />
                </a>
              </div>
            ) : (
              <div className="text-center py-4">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600 mb-3">Nenhum currículo enviado</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(editPath)}
                  className="text-cyan-600 border-cyan-200"
                >
                  Enviar Currículo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6. BOTÕES / AÇÕES */}
        <div className="space-y-3 pt-2">
          <Button 
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            onClick={() => navigate(editPath)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar Perfil Completo
          </Button>
          
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => navigate('/m/disc')}
          >
            <Brain className="w-4 h-4 mr-2" />
            {profile.disc_profile ? 'Refazer Avaliação DISC' : 'Fazer Avaliação DISC'}
          </Button>
        </div>

        {/* Privacy Notice */}
        <Card className="border-cyan-200 bg-cyan-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-cyan-900 text-sm">Privacidade Protegida</h3>
                <p className="text-xs text-cyan-700 mt-1">
                  <strong>Dados pessoais</strong> (nome, e-mail, celular, empresa atual) são <strong>invisíveis</strong> para empresas 
                  até que você <strong>aprove explicitamente</strong> uma solicitação de acesso.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
